import { useEffect, useRef, useState, type JSX, type ReactNode } from 'react';
import { Button, CheckIcon, CloseIcon, PencilIcon, Textarea } from '@viniciomendesr/rayrovisk-design-system';
import { ScriptBody, parseScript } from './ScriptBody';

/**
 * Headings the draft dropped. The script is stored as Markdown and drawn from
 * its `##` lines, so deleting one silently merges two sections into whichever
 * heading survived above it: the edit looks fine in the box and comes out
 * wrong on screen.
 */
export function missingHeadings(original: string, draft: string): string[] {
  const present = new Set(parseScript(draft).map((section) => section.title));
  return parseScript(original)
    .map((section) => section.title)
    .filter((title) => !present.has(title));
}

export interface ScriptEditorProps {
  /** What the service returned. Editing never rewrites it, so Desfazer is free. */
  original: string;
  value: string;
  onChange(value: string): void;
  /** Copy, pin and regenerate, rendered under the script in read mode. */
  actions: ReactNode;
}

/**
 * Free-text editing of a generated script, guarded rather than structured.
 *
 * The prompt output is edited marker by marker because a prompt is a template
 * with named holes. A script is prose: she rewrites a sentence, joins two
 * blocks, cuts the closing. One box is the honest shape for that, and the
 * guard is what keeps one box from breaking the rendering.
 */
export function ScriptEditor({ original, value, onChange, actions }: ScriptEditorProps): JSX.Element {
  const [draft, setDraft] = useState<string | null>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const editing = draft !== null;

  useEffect(() => {
    if (editing) field.current?.focus();
  }, [editing]);

  if (draft === null) {
    return (
      <>
        <ScriptBody text={value} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Button variant="secondary" iconStart={<PencilIcon size={14} />} onClick={() => setDraft(value)}>
            Editar roteiro
          </Button>
          {value !== original && (
            <Button variant="ghost" onClick={() => onChange(original)}>
              Desfazer edição
            </Button>
          )}
          {actions}
        </div>
      </>
    );
  }

  const missing = missingHeadings(original, draft);
  const blocked = missing.length > 0 || draft.trim() === '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Textarea
        ref={field}
        aria-label="Roteiro em edição"
        rows={14}
        autoGrow
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            setDraft(null);
          } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && !blocked) {
            event.preventDefault();
            onChange(draft);
            setDraft(null);
          }
        }}
      />
      <p
        role={missing.length > 0 ? 'alert' : undefined}
        style={{
          margin: 0,
          font: '400 12.5px/1.5 var(--rvk-font-functional)',
          color: missing.length > 0 ? 'var(--rvk-color-error, #b3261e)' : 'var(--rvk-text-secondary)'
        }}
      >
        {missing.length > 0
          ? `Faltam os títulos ${missing.join(', ')}. Cada um começa com ## na própria linha, e sem eles o roteiro sai como um bloco só.`
          : 'As linhas que começam com ## são os títulos. O resto é a sua fala.'}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Button
          variant="primary"
          iconStart={<CheckIcon size={14} />}
          access={blocked ? 'unavailable' : undefined}
          accessReason={
            draft.trim() === '' ? 'O roteiro está vazio.' : 'Devolva os títulos que faltam para salvar.'
          }
          onClick={() => {
            onChange(draft);
            setDraft(null);
          }}
        >
          Salvar
        </Button>
        <Button variant="ghost" iconStart={<CloseIcon size={14} />} onClick={() => setDraft(null)}>
          Cancelar
        </Button>
        {missing.length > 0 && (
          <Button variant="secondary" onClick={() => setDraft(original)}>
            Restaurar o original
          </Button>
        )}
      </div>
    </div>
  );
}
